import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { ListItemText, Tooltip, Typography } from '@material-ui/core';
import DropDownMenu from './DropDownMenu'
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Collapse from '@material-ui/core/Collapse';
import clsx from 'classnames';
import variables from '../../../Sys/variable.scss';

const useStyles = makeStyles((theme) => ({
    menuItem: {
        marginBottom: theme.spacing(0.5),
        borderRadius: 8,
        minHeight: 42,
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
            backgroundColor: `${variables.sidebarActive} !important`,
            '&:hover': {
                backgroundColor: `${variables.sidebarActive} !important`,
            },
        },
        '&.Mui-selected $menuItemText, &.Mui-selected $menuItemText.MuiTypography-body1': {
            color: '#fff',
        },
        '&.Mui-selected $menuIcon': {
            color: '#fff',
            opacity: 1,
        },
        '&.Mui-selected $expandIcon': {
            color: '#fff',
            opacity: 1,
        },
    },
    menuItemCollapsed: {
        justifyContent: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        minHeight: 44,
    },
    menuIcon: {
        minWidth: 36,
        color: variables.darkThemeText,
        opacity: 0.78,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
        },
    },
    menuIconCollapsed: {
        minWidth: 0,
        justifyContent: 'center',
        opacity: 0.85,
        '& .MuiSvgIcon-root': {
            fontSize: '1.35rem',
        },
    },
    menuItemText: {
        color: variables.darkThemeText,
        fontSize: '0.8125rem',
        fontWeight: 500,
        lineHeight: 1.35,
        '&.MuiTypography-body1': {
            color: variables.darkThemeText,
            fontSize: '0.8125rem',
            fontWeight: 500,
            lineHeight: 1.35,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.8125rem',
            '&.MuiTypography-body1': {
                fontSize: '0.8125rem',
            },
        },
    },
    expandIcon: {
        color: variables.darkThemeText,
        fontSize: '1.25rem',
        opacity: 0.7,
    },
    badge: {
        backgroundColor: '#EC7A12',
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        padding: '3px 6px',
        borderRadius: 4,
        letterSpacing: 0.3,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
}));

const isMenuItemSelected = (item, pathname) => {
    if (pathname.startsWith(item.active)) {
        return true;
    }
    return Array.isArray(item.alsoActive) && item.alsoActive.some((path) => pathname.startsWith(path));
};

const MenuItems = ({ menuItems, index, handleItemClick, location, collapsed = false, onRequestExpand = () => {} }) => {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const isSelected = isMenuItemSelected(menuItems, location.pathname);

    const handleClick = () => {
        setOpen(!open);
    };

    React.useEffect(() => {
        if (isSelected) {
            setOpen(true);
        }
    }, [location, isSelected]);

    if (collapsed) {
        const handleCollapsedClick = () => {
            if (menuItems.submenus) {
                onRequestExpand();
            } else {
                handleItemClick(menuItems.active);
            }
        };

        return (
            <Tooltip title={menuItems.tooltip || menuItems.title} placement="right">
                <ListItem
                    data-testid={`sidebar-item-${menuItems.title.toLowerCase().replace(/\s+/g, '-')}`}
                    selected={isSelected}
                    className={clsx(classes.menuItem, classes.menuItemCollapsed)}
                    onClick={handleCollapsedClick}
                    button
                    key={index}
                >
                    <ListItemIcon
                        className={clsx(classes.menuIcon, classes.menuIconCollapsed)}
                    >
                        {menuItems.icon}
                    </ListItemIcon>
                </ListItem>
            </Tooltip>
        );
    }

    return (
        <>
            {menuItems && menuItems.submenus ? (
                <>
                    <ListItem
                        data-testid={`sidebar-item-${menuItems.title.toLowerCase().replace(/\s+/g, '-')}`}
                        selected={isSelected}
                        className={classes.menuItem}
                        onClick={handleClick}
                        button
                        key={index}
                    >
                        <ListItemIcon className={classes.menuIcon}>{menuItems.icon}</ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography className={classes.menuItemText}>{menuItems.title}</Typography>
                            }
                        />
                        {open
                            ? <ExpandLess className={classes.expandIcon} />
                            : <ExpandMore className={classes.expandIcon} />}
                    </ListItem>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <DropDownMenu submenus={menuItems.submenus} handleItemClick={handleItemClick} location={location} />
                        </List>
                    </Collapse>
                </>
            ) : (
                <ListItem
                    data-testid={`sidebar-item-${menuItems.title.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleItemClick(menuItems.active)}
                    className={classes.menuItem}
                    selected={isSelected}
                    button
                    key={index}
                >
                    <ListItemIcon className={classes.menuIcon}>{menuItems.icon}</ListItemIcon>
                    <ListItemText
                        primary={
                            <span className={classes.titleRow}>
                                <Typography className={classes.menuItemText}>
                                    {menuItems.title}
                                </Typography>
                                {menuItems.badge ? (
                                    <span className={classes.badge}>
                                        {menuItems.badge}
                                    </span>
                                ) : null}
                            </span>
                        }
                    />
                </ListItem>
            )}
        </>
    );
}

export default MenuItems;
